
import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Dashboard from '@/pages/Dashboard';
import Timeline from '@/pages/Timeline';
import Friends from '@/pages/Friends';
import Profile from '@/pages/Profile';
import Notifications from "@/pages/Notifications";
import WheelPickerTest from '@/pages/WheelPickerTest';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AuthVerify from '@/pages/AuthVerify';
import ResetPassword from '@/pages/ResetPassword';
import ForgotPassword from '@/pages/ForgotPassword';
import NotFound from '@/pages/NotFound';
import LandingPage from '@/pages/LandingPage';
import Invite from '@/pages/Invite';
import TermsOfService from '@/pages/TermsOfService';
import TermsOfServiceEn from '@/pages/TermsOfServiceEn';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import PrivacyPolicyEn from '@/pages/PrivacyPolicyEn';
import About from '@/pages/About';
import AboutEn from '@/pages/AboutEn';
import Contact from '@/pages/Contact';
import ContactEn from '@/pages/ContactEn';
import Help from '@/pages/Help';
import HelpEn from '@/pages/HelpEn';
import FAQ from '@/pages/FAQ';
import FAQEn from '@/pages/FAQEn';
import Changelog from '@/pages/Changelog';
import TimelineEntry from '@/pages/TimelineEntry';
import ShareTimeline from '@/pages/ShareTimeline';
import ProfileHandover from '@/pages/ProfileHandover';
import EntryApproval from '@/pages/EntryApproval';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { EfficientRealtimeProvider } from '@/contexts/EfficientRealtimeContext';
import HomeRoute from './HomeRoute';
import IntelligentHomeRoute from './IntelligentHomeRoute';
import ProtectedRoute from './ProtectedRoute';
import AdminProtectedRoute from './AdminProtectedRoute';
import AdminTickets from '@/pages/AdminTickets';
import '@/lib/i18n';

const queryClient = new QueryClient();

const DomainRouter = () => {
  const [currentDomain, setCurrentDomain] = useState<string>('');

  useEffect(() => {
    const hostname = window.location.hostname;
    setCurrentDomain(hostname);
    console.log('DomainRouter - detected hostname:', hostname);
    console.log('DomainRouter - full URL:', window.location.href);
  }, []);

  // Landing page domains - www.uplivion.life should always show landing page
  const isLandingDomain = currentDomain === 'www.uplivion.life';
  
  // Root domain - uplivion.life should be intelligent router
  const isRootDomain = currentDomain === 'uplivion.life';
  
  // App domains - app.uplivion.life and development domains
  const isAppDomain = currentDomain === 'app.uplivion.life' || 
                     currentDomain.includes('lovable') || 
                     currentDomain.includes('localhost') ||
                     currentDomain.includes('127.0.0.1');

  console.log('DomainRouter - Domain classification:', {
    currentDomain,
    isLandingDomain,
    isRootDomain,
    isAppDomain
  });

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <EfficientRealtimeProvider>
          <NotificationProvider>
          {isLandingDomain ? (
            // Landing page routes - always show landing page (auth-aware)
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-en" element={<TermsOfServiceEn />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-en" element={<PrivacyPolicyEn />} />
              <Route path="/about" element={<About />} />
              <Route path="/about-en" element={<AboutEn />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/contact-en" element={<ContactEn />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help-en" element={<HelpEn />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/faq-en" element={<FAQEn />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="*" element={<LandingPage />} />
            </Routes>
          ) : isRootDomain ? (
            // Root domain routes - intelligent routing based on auth status
            <Routes>
              <Route path="/" element={<IntelligentHomeRoute />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-en" element={<TermsOfServiceEn />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-en" element={<PrivacyPolicyEn />} />
              <Route path="/about" element={<About />} />
              <Route path="/about-en" element={<AboutEn />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/contact-en" element={<ContactEn />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help-en" element={<HelpEn />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/faq-en" element={<FAQEn />} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="*" element={<IntelligentHomeRoute />} />
            </Routes>
          ) : (
            // App routes - full application functionality
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/invite" element={<Invite />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth-verify" element={<AuthVerify />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-en" element={<TermsOfServiceEn />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-en" element={<PrivacyPolicyEn />} />
              <Route path="/about" element={<About />} />
              <Route path="/about-en" element={<AboutEn />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/contact-en" element={<ContactEn />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help-en" element={<HelpEn />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/faq-en" element={<FAQEn />} />
              <Route path="/changelog" element={<Changelog />} />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/timeline" element={
                <ProtectedRoute>
                  <Timeline />
                </ProtectedRoute>
              } />
              <Route path="/timeline/:username/entry/:entryId" element={
                <ProtectedRoute>
                  <Timeline />
                </ProtectedRoute>
              } />
              <Route path="/timeline/:username" element={
                <ProtectedRoute>
                  <Timeline />
                </ProtectedRoute>
              } />
              <Route path="/share/timeline/:username" element={
                <ProtectedRoute>
                  <ShareTimeline />
                </ProtectedRoute>
              } />
              <Route path="/handover/:token" element={
                <ProtectedRoute>
                  <ProfileHandover />
                </ProtectedRoute>
              } />
              <Route path="/approve/:entryId" element={
                <ProtectedRoute>
                  <EntryApproval />
                </ProtectedRoute>
              } />
              <Route path="/friends" element={
                <ProtectedRoute>
                  <Friends />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              } />
              <Route path="/wheel-picker-test" element={
                <ProtectedRoute>
                  <WheelPickerTest />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <AdminTickets />
                </AdminProtectedRoute>
              } />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          )}
          </NotificationProvider>
        </EfficientRealtimeProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default DomainRouter;
